import { useMemo } from 'react';
import { urlMiddleware } from 'react-relay-network-modern/es';
import type { Middleware } from 'react-relay-network-modern/es';

import { RelayEnvironmentProvider } from 'react-relay/hooks';
import type { ComponentEnhancer } from 'recompose';

import { useExtendedLogger } from '../../Logger';

import { initEnvironment } from '../helpers';
import { LOGGER_NAME } from '../constants';
import { NetworkOptions } from '../types';
import { appendQueryName } from '../helpers/middlewares/appendQueryName';
import { EnvironmentConfig } from 'relay-runtime/lib/store/RelayModernEnvironment';

const withRelay = <P extends {}>({
  graphqlEndpoint,
  middlewares = [],
  options = {},
  environmentOptions,
}: {
  graphqlEndpoint: string;
  middlewares?: Middleware[];
  options?: NetworkOptions;
  environmentOptions?: EnvironmentConfig;
}) => {
  const enhancer = (Component: React.ComponentType<P>) => (props: P) => {
    const log = useExtendedLogger(LOGGER_NAME);

    const environment = useMemo(() => {
      log('initialising environment with %s graphql endpoint', graphqlEndpoint);

      return initEnvironment({
        network: {
          middlewares: [
            urlMiddleware({ url: graphqlEndpoint, credentials: 'include' }),
            ...middlewares,
            appendQueryName(),
          ],
          options,
        },
        environmentOptions,
      });
    }, [log]);

    log('adding provider with %o environment', environment);

    return (
      <RelayEnvironmentProvider environment={environment}>
        <Component {...props} />
      </RelayEnvironmentProvider>
    );
  };
  return (enhancer as unknown) as ComponentEnhancer<P, P>;
};

export default withRelay;
